import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import { MasterDataValue } from '../../types/mdm';
import { SecondaryButton } from '../common/ActionButtons';

interface RevertOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  overrideValue: MasterDataValue | null;
  globalValue: MasterDataValue | null;
  loading?: boolean;
}

export const RevertOverrideDialog: React.FC<RevertOverrideDialogProps> = ({
  open,
  onClose,
  onConfirm,
  overrideValue,
  globalValue,
  loading = false,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RestoreIcon />
          Revert to Global Value
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This operation cannot be undone. The override will be permanently removed.
        </Alert>

        <Typography variant="body1" gutterBottom>
          Are you sure you want to revert this override back to the global value?
        </Typography>

        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}
        >
          <Box>
            <Card variant="outlined">
              <CardHeader
                title="Current Override"
                titleTypographyProps={{ variant: 'h6' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography>
                  <strong>Name:</strong> {overrideValue?.name || 'N/A'}
                </Typography>
                <Typography>
                  <strong>Scope:</strong> OVERRIDDEN
                </Typography>
                <Typography>
                  <strong>Tenant:</strong> {overrideValue?.tenant_id || 'N/A'}
                </Typography>
                <Typography>
                  <strong>Status:</strong> {overrideValue?.is_active ? 'Active' : 'Inactive'}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Card variant="outlined">
              <CardHeader
                title="Global Value (Will Become Visible)"
                titleTypographyProps={{ variant: 'h6' }}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 0 }}>
                <Typography>
                  <strong>Name:</strong> {globalValue?.name || 'Loading...'}
                </Typography>
                <Typography>
                  <strong>Scope:</strong> GLOBAL
                </Typography>
                <Typography>
                  <strong>Tenant:</strong> GLOBAL
                </Typography>
                <Typography>
                  <strong>Status:</strong> {globalValue?.is_active ? 'Active' : 'Inactive'}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <SecondaryButton onClick={onClose} disabled={loading}>
          Cancel
        </SecondaryButton>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <RestoreIcon />}
        >
          {loading ? 'Reverting...' : 'Revert to Global'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevertOverrideDialog;
